import { PatientHistoryType } from 'src/graphql.generated'
import { PatientHistoryEntry } from '../api/useAdvancedPatientHistory'
import { toIso } from '../format'
import { valueOf } from '../panels/patientHistory/EntryBody'

export type ScoreType = 'PHQ9' | 'GAD7'

export const SCORE_TYPES: Record<ScoreType, { label: string; maxScore: number; historyType: PatientHistoryType }> = {
  PHQ9: { label: 'PHQ-9', maxScore: 27, historyType: PatientHistoryType.Phq9 },
  GAD7: { label: 'GAD-7', maxScore: 21, historyType: PatientHistoryType.Gad7 },
}

export type ScorePoint = { id: string; date: string; score: number }

const millis = (iso: string | null): number => (iso ? Date.parse(iso) || 0 : 0)

// Oldest first, as the graph reads left to right.
export const scorePoints = (entries: PatientHistoryEntry[], type: ScoreType): ScorePoint[] =>
  entries
    .flatMap((entry) => {
      const score = entry.type === SCORE_TYPES[type].historyType ? valueOf(entry)?.[`${type}_SCORE`] : undefined
      const date = toIso(entry.date)

      return typeof score === 'number' && Number.isFinite(score) && date
        ? [{ id: entry.entryID, date, score, createdAt: toIso(entry.createdAt) }]
        : []
    })
    .sort((a, b) => millis(a.date) - millis(b.date) || millis(a.createdAt) - millis(b.createdAt))
    .map(({ id, date, score }) => ({ id, date, score }))

// A swing inside this band is flat: the stalled-chip yellow, not green or red.
export const TOTAL_CHANGE_FLAT_WITHIN = 5

export type TotalChangeTone = 'improved' | 'flat' | 'worse'

// Lower is better for these scores, so a drop is the improvement.
export const totalChangeTone = (change: number): TotalChangeTone => {
  if (Math.abs(change) <= TOTAL_CHANGE_FLAT_WITHIN) {
    return 'flat'
  }

  return change < 0 ? 'improved' : 'worse'
}

export const totalChangePercent = (points: ScorePoint[]): number | null => {
  if (points.length < 2 || points[0].score === 0) {
    return null
  }
  const change = Math.round((points[points.length - 1].score / points[0].score - 1) * 100)

  return Number.isFinite(change) ? change : null
}

// First to last, as a rounded percentage, the way Standard's graph states "total change".
export const totalChangeLabel = (points: ScorePoint[]): string | null => {
  if (points.length < 2) {
    return null
  }
  const change = totalChangePercent(points)

  return change === null ? 'N/A' : `${change}%`
}
